import React, { useEffect, useState, useCallback } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import Loader from "rsuite/Loader";
import MeetingFormModal from "./MeetingFormModal";
import { useMeetingList } from "../../../../reducers/meetingList";

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

/* ─────────────────────────────────────────
   Custom event renderer – colour by organiser
   ───────────────────────────────────────── */
const EventComponent = ({ event }) => (
  <span title={event.title} style={{ fontSize: "12px" }}>
    {event.title}
  </span>
);

const eventStyleGetter = (event) => ({
  style: {
    backgroundColor: event.is_organizer ? "#006EFA" : "#6c757d",
    borderColor: event.is_organizer ? "#005BD1" : "#5a6268",
    color: "#fff",
    borderRadius: "4px",
    border: "none",
    fontSize: "12px",
  },
});

/* ─────────────────────────────────────────
   Custom Toolbar
   ───────────────────────────────────────── */
const CustomToolbar = (toolbar) => {
  const goToBack = () => {
    toolbar.onNavigate("PREV");
  };

  const goToNext = () => {
    toolbar.onNavigate("NEXT");
  };

  const goToView = (view) => {
    toolbar.onView(view);
  };

  const label = () => {
    const date = moment(toolbar.date);
    return (
      <span style={{ fontSize: "18px", fontWeight: 600, color: "#333" }}>
        {toolbar.label}
      </span>
    );
  };

  return (
    <div
      className="rbc-toolbar"
      style={{
        marginBottom: "20px",
        borderBottom: "1px solid #eee",
        paddingBottom: "10px",
      }}
    >
      <span className="rbc-btn-group">
        <button type="button" onClick={goToBack} style={toolbarStyles.navBtn}>
          <MdChevronLeft size={24} />
        </button>
        <button type="button" onClick={goToNext} style={toolbarStyles.navBtn}>
          <MdChevronRight size={24} />
        </button>
      </span>

      <span className="rbc-toolbar-label">{label()}</span>

      <span className="rbc-btn-group">
        <button
          type="button"
          onClick={() => goToView(Views.MONTH)}
          className={toolbar.view === Views.MONTH ? "rbc-active" : ""}
          style={
            toolbar.view === Views.MONTH
              ? toolbarStyles.activeBtn
              : toolbarStyles.viewBtn
          }
        >
          Month
        </button>
        <button
          type="button"
          onClick={() => goToView(Views.WEEK)}
          className={toolbar.view === Views.WEEK ? "rbc-active" : ""}
          style={
            toolbar.view === Views.WEEK
              ? toolbarStyles.activeBtn
              : toolbarStyles.viewBtn
          }
        >
          Week
        </button>
        <button
          type="button"
          onClick={() => goToView(Views.DAY)}
          className={toolbar.view === Views.DAY ? "rbc-active" : ""}
          style={
            toolbar.view === Views.DAY
              ? toolbarStyles.activeBtn
              : toolbarStyles.viewBtn
          }
        >
          Day
        </button>
      </span>
    </div>
  );
};

const toolbarStyles = {
  navBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px",
    borderRadius: "50%",
    border: "1px solid #ddd",
    background: "#fff",
    cursor: "pointer",
    marginRight: "8px",
    transition: "background 0.2s",
  },
  viewBtn: {
    padding: "6px 16px",
    fontSize: "14px",
    border: "1px solid #ddd",
    background: "#fff",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  activeBtn: {
    padding: "6px 16px",
    fontSize: "14px",
    border: "1px solid #006EFA",
    background: "#006EFA",
    color: "#fff",
    cursor: "pointer",
    transition: "all 0.2s",
  },
};

/* ─────────────────────────────────────────
   Main component
   ───────────────────────────────────────── */
const MeetingCalendarView = ({ currentUser, customerDetails }) => {
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState(Views.WEEK);
  const [currentDate, setCurrentDate] = useState(new Date());

  // Form modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [startInViewMode, setStartInViewMode] = useState(false);

  const { events, loading, fetchMeetings } = useMeetingList();

  const handleSelectSlot = useCallback(({ start, end }) => {
    setModalData({ start, end });
    setStartInViewMode(false);
    setShowFormModal(true);
  }, []);

  const handleSelectEvent = (event) => {
    setModalData(event);
    setStartInViewMode(true);
    setShowFormModal(true);
  };

  const onEventDrop = useCallback(
    ({ event, start, end, isAllDay: droppedAnyday }) => {
      const updatedEvent = { ...event, start, end, allDay: droppedAnyday };
      setModalData(updatedEvent);
      setStartInViewMode(false);
      setShowFormModal(true);
    },
    [],
  );

  if (loading && events.length === 0) {
    return (
      <div style={styles.loaderWrap}>
        <Loader size="md" content="Loading meetings…" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorWrap}>
        <p style={{ color: "#dc3545" }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Legend */}
      <div style={styles.legend}>
        <span style={{ ...styles.dot, background: "#006EFA" }} />
        <span style={styles.legendText}>Organised by me</span>
        <span
          style={{ ...styles.dot, background: "#6c757d", marginLeft: 14 }}
        />
        <span style={styles.legendText}>Invited</span>
      </div>

      {/* Calendar */}
      <div style={styles.layout}>
        <style>{`
          .rbc-month-row { min-height: 100px; }
          .rbc-show-more { color: #006EFA; font-weight: 600; font-size: 11px; }
          ${
            currentView !== Views.MONTH
              ? ".rs-modal-body { overflow: hidden !important; }"
              : ""
          }
        `}</style>
        <div style={{ flex: 1, minWidth: 0 }}>
          <DnDCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: "70vh" }}
            view={currentView}
            onView={setCurrentView}
            date={currentDate}
            onNavigate={(newDate, view, action) => {
              setCurrentDate(newDate);
              if (action === "TODAY") setCurrentView(Views.DAY);
            }}
            views={[Views.MONTH, Views.WEEK, Views.DAY]}
            eventPropGetter={eventStyleGetter}
            components={{
              event: EventComponent,
              toolbar: CustomToolbar,
            }}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            onShowMore={(eventsOnDay, date) => {
              setCurrentDate(date);
              setCurrentView(Views.DAY);
            }}
            onEventDrop={onEventDrop}
            scrollToTime={new Date()}
            resizable={false}
            selectable
          />
        </div>
      </div>

      <MeetingFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        initialData={modalData}
        onSuccess={fetchMeetings}
        startInViewMode={startInViewMode}
        currentUser={currentUser}
        customerEmail={customerDetails.email}
        customerDetails={customerDetails}
      />
    </div>
  );
};

/* ─────────────────────────────────────────
   Inline styles
   ───────────────────────────────────────── */
const styles = {
  container: { padding: "4px 0" },
  loaderWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "240px",
  },
  errorWrap: { textAlign: "center", padding: "32px" },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "12px",
    fontSize: "12px",
    color: "#555",
  },
  dot: {
    display: "inline-block",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
  },
  legendText: { fontSize: "12px", color: "#555" },
  layout: { display: "block" },
};

export default MeetingCalendarView;



// WEBPACK FOOTER //
// ./src/components/crm/leads/components/MeetingCalendarView.js